import React, { useState, useEffect, useRef } from 'react';
import { Phone, ArrowRight, AlertCircle, CheckCircle, RotateCcw } from 'lucide-react';

import { ROUTES } from '../../constants/routes.constants';

const API_BASE_URL = ROUTES.API_BASE_URL;

const OTPVerification = ({ userId, phoneNumber, countryCode, onVerified, onClose }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [remainingAttempts, setRemainingAttempts] = useState(5);
  const inputRefs = useRef([]);
  const countdownInterval = useRef(null);
  // L'OTP est déjà envoyé lors de l'inscription (backend)
  // Démarrer le countdown immédiatement
  useEffect(() => {
    setCountdown(600); // 10 minutes
    
    return () => {
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
      }
    };
  }, []);

  // Gérer le countdown
  useEffect(() => {
    if (countdown > 0) {
      countdownInterval.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
      }
    };
  }, [countdown]);

  const sendOTP = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/auth/otp/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          phoneNumber,
          countryCode,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Afficher l'erreur détaillée si disponible
        const errorMessage = data.message || data.error?.message || 'Failed to send OTP';
        throw new Error(errorMessage);
      }

      // Démarrer le countdown (10 minutes = 600 secondes)
      setCountdown(600);
    } catch (err) {
      setError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const resendOTP = async () => {
    if (countdown > 0) return; // Ne pas permettre le renvoi si le countdown est actif

    try {
      setIsResending(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/auth/otp/resend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to resend OTP');
      }

      // Réinitialiser le formulaire et le countdown
      setOtp(['', '', '', '', '', '']);
      setCountdown(600);
      setRemainingAttempts(5);
      inputRefs.current[0]?.focus();
    } catch (err) {
      setError(err.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleOtpChange = (index, value) => {
    // Ne permettre que les chiffres
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError(null);

    // Passer automatiquement au champ suivant
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Supprimer et revenir au champ précédent
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    
    if (/^\d{6}$/.test(pastedData)) {
      const newOtp = pastedData.split('');
      setOtp(newOtp);
      inputRefs.current[5]?.focus();
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    
    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/auth/otp/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: phoneNumber,
          otp_value: otpCode,
          phone_country: countryCode,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.data?.remainingAttempts !== undefined) {
          setRemainingAttempts(data.data.remainingAttempts);
        }
        throw new Error(data.message || 'Invalid OTP code');
      }

      // Succès
      setSuccess(true);
      setTimeout(() => {
        if (onVerified) onVerified();
      }, 1500);
    } catch (err) {
      setError(err.message || 'Invalid OTP code');
      // Réinitialiser le code OTP
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formattedPhone = `${countryCode} ${phoneNumber}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="mx-auto w-16 h-16 bg-[#27489E]/10 rounded-full flex items-center justify-center mb-4">
          <Phone className="w-8 h-8 text-[#27489E]" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Vérification du numéro de téléphone
        </h2>
        <p className="text-gray-600 text-sm">
          Nous avons envoyé un code à 6 chiffres au
        </p>
        <p className="text-gray-800 font-semibold mt-1">
          {formattedPhone}
        </p>
      </div>

      {/* Message d'erreur */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <div className="flex-1">
            <span className="text-sm">{error}</span>
            {remainingAttempts < 5 && (
              <p className="text-xs mt-1">
                Tentatives restantes: {remainingAttempts}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Message de succès */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl flex items-center gap-2">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">Numéro de téléphone vérifié avec succès !</span>
        </div>
      )}

      {/* Formulaire OTP */}
      {!success && (
        <form onSubmit={handleVerify} className="space-y-6">
          {/* Champs OTP */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
              Entrez le code de vérification
            </label>
            <div className="flex justify-center gap-2">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className={`w-12 h-14 text-center text-2xl font-bold rounded-xl border-2 transition ${
                    error
                      ? 'border-red-500 bg-red-50'
                      : digit
                      ? 'border-[#27489E] bg-[#27489E]/5'
                      : 'border-gray-200 bg-white'
                  } focus:border-[#27489E] focus:ring-2 focus:ring-[#27489E]/20 outline-none`}
                />
              ))}
            </div>
          </div>

          {/* Countdown et Resend */}
          <div className="text-center space-y-3">
            {countdown > 0 ? (
              <p className="text-sm text-gray-600">
                Vous pouvez demander un nouveau code dans{' '}
                <span className="font-semibold text-[#27489E]">{formatTime(countdown)}</span>
              </p>
            ) : (
              <button
                type="button"
                onClick={resendOTP}
                disabled={isResending}
                className="text-sm text-[#27489E] hover:text-[#1e3a7d] font-medium flex items-center gap-2 mx-auto disabled:opacity-50"
              >
                <RotateCcw className={`w-4 h-4 ${isResending ? 'animate-spin' : ''}`} />
                {isResending ? 'Envoi en cours...' : 'Renvoyer le code'}
              </button>
            )}
          </div>

          {/* Bouton de vérification */}
          <button
            type="submit"
            disabled={isLoading || otp.join('').length !== 6}
            className="w-full bg-gradient-to-r from-[#27489E] to-[#1e3a7d] text-white py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-[#27489E]/30 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : (
              <>
                Vérifier
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </form>
      )}

      {/* Bouton retour */}
      {onClose && (
        <button
          onClick={onClose}
          className="w-full text-sm text-gray-600 hover:text-gray-800 font-medium"
        >
          Modifier le numéro de téléphone
        </button>
      )}
    </div>
  );
};

export default OTPVerification;

